import { useState, useEffect } from 'react'
import { Target, Plus, Edit2, Trash2, Save, X, Users, Calendar, TrendingUp } from 'lucide-react'
import { apiService } from '../services/api'
import ProductGroupSelector from '../components/ui/ProductGroupSelector'

const OPERATORS = [
  { code: 'ORANGE', name: 'Orange', color: 'orange', supportsIndividualGoals: true },
  { code: 'PLUS', name: 'Plus', color: 'green', supportsIndividualGoals: true },
  { code: 'TMOBILE', name: 'T-Mobile', color: 'pink', supportsIndividualGoals: false },
  { code: 'P4', name: 'Play', color: 'purple', supportsIndividualGoals: true },
  { code: 'TOTAL', name: 'Total', color: 'blue', supportsIndividualGoals: false }
]

const MONTHS = [
  { value: 1, label: 'Styczeń' },
  { value: 2, label: 'Luty' },
  { value: 3, label: 'Marzec' },
  { value: 4, label: 'Kwiecień' },
  { value: 5, label: 'Maj' },
  { value: 6, label: 'Czerwiec' },
  { value: 7, label: 'Lipiec' },
  { value: 8, label: 'Sierpień' },
  { value: 9, label: 'Wrzesień' },
  { value: 10, label: 'Październik' },
  { value: 11, label: 'Listopad' },
  { value: 12, label: 'Grudzień' }
]

export default function GoalsManagementPage() {
  const [selectedOperator, setSelectedOperator] = useState('ORANGE')
  const [goals, setGoals] = useState([])
  const [kams, setKams] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [goalType, setGoalType] = useState('all') // 'all', 'individual', 'team'
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1)
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
  const [selectedProductGroup, setSelectedProductGroup] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [editingGoal, setEditingGoal] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  useEffect(() => {
    loadKams()
  }, [])

  useEffect(() => {
    loadGoals()
  }, [selectedOperator, goalType, selectedMonth, selectedYear, selectedProductGroup])

  const handleOperatorChange = (code) => {
    const operatorData = OPERATORS.find(op => op.code === code)
    // Batch both updates so loadGoals fires once with clean state
    setSelectedOperator(code)
    setSelectedProductGroup(null)
    if (!operatorData?.supportsIndividualGoals && goalType === 'individual') {
      setGoalType('team')
    }
  }

  const loadKams = async () => {
    try {
      const response = await apiService.getKamList()
      setKams(response.data.kams)
    } catch (err) {
      console.error('Error loading KAMs:', err)
    }
  }

  const loadGoals = async () => {
    try {
      setLoading(true)
      setError(null)

      const params = {
        operator_code: selectedOperator,
        month: selectedMonth,
        year: selectedYear
      }

      if (goalType !== 'all') {
        params.goal_type = goalType
      }
      if (selectedProductGroup) {
        params.product_group = selectedProductGroup
      }

      const response = await apiService.getGoalsV2(params)
      setGoals(response.data.goals)
    } catch (err) {
      setError('Nie udało się załadować celów')
      console.error('Error loading goals:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateGoal = () => {
    setEditingGoal(null)
    setShowModal(true)
  }

  const handleEditGoal = (goal) => {
    setEditingGoal(goal)
    setShowModal(true)
  }

  const handleDeleteGoal = async (goal) => {
    try {
      await apiService.deleteGoalV2(goal.id)
      await loadGoals()
      setDeleteConfirm(null)
      alert('Cel został usunięty')
    } catch (err) {
      console.error('Error deleting goal:', err)
      alert('Błąd podczas usuwania celu: ' + (err.response?.data?.detail || err.message))
    }
  }

  const handleSaveGoal = async (goalData) => {
    try {
      if (editingGoal) {
        await apiService.updateGoalV2(editingGoal.id, goalData)
      } else {
        await apiService.createGoalV2({
          operator_code: selectedOperator,
          product_group: goalData.product_group || selectedProductGroup || 'DEFAULT',
          miesiac: goalData.miesiac,
          rok: goalData.rok,
          wartosc: goalData.wartosc,
          wartosc_target: goalData.wartosc_target,
          dodinfo: goalData.dodinfo,
        })
      }
      setShowModal(false)
      setEditingGoal(null)
      await loadGoals()
    } catch (err) {
      console.error('Error saving goal:', err)
      alert('Błąd podczas zapisywania celu: ' + (err.response?.data?.detail || err.message))
    }
  }

  const formatValue = (value) => {
    return new Intl.NumberFormat('pl-PL').format(value)
  }

  const getKamName = (kamCode) => {
    const kam = kams.find(k => k.kam_code === kamCode)
    return kam ? kam.kam_name : kamCode
  }

  // Generate years: current year - 1 to current year + 2
  const currentYear = new Date().getFullYear()
  const years = [currentYear - 1, currentYear, currentYear + 1, currentYear + 2]
  
  // Check if selected operator supports individual goals
  const selectedOperatorData = OPERATORS.find(op => op.code === selectedOperator)
  const supportsIndividualGoals = selectedOperatorData?.supportsIndividualGoals ?? true

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Target className="w-8 h-8 text-blue-600" />
              Zarządzanie celami KAM
            </h1>
            <p className="text-gray-600 mt-1">
              Zarządzanie celami sprzedażowymi dla KAMów w poszczególnych sieciach
            </p>
          </div>
          <button
            onClick={handleCreateGoal}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Dodaj cel
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* Month selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Miesiąc
            </label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {MONTHS.map(month => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
          </div>

          {/* Year selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rok
            </label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {years.map(year => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          {/* Goal type filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Typ celu
            </label>
            <select
              value={goalType}
              onChange={(e) => setGoalType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Wszystkie</option>
              {supportsIndividualGoals && (
                <option value="individual">Cele indywidualne (KAM)</option>
              )}
              <option value="team">Cele zespołowe (Team)</option>
            </select>
            {!supportsIndividualGoals && (
              <p className="mt-1 text-xs text-gray-500">
                Ten operator obsługuje tylko cele zespołowe
              </p>
            )}
          </div>

          {/* Product group selector */}
          <ProductGroupSelector
            operatorCode={selectedOperator}
            value={selectedProductGroup}
            onChange={setSelectedProductGroup}
          />
        </div>
      </div>

      {/* Operator Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {OPERATORS.map((operator) => (
              <button
                key={operator.code}
                onClick={() => handleOperatorChange(operator.code)}
                className={`
                  py-4 px-1 border-b-2 font-medium text-sm transition-colors
                  ${selectedOperator === operator.code
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                {operator.name}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Goals Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Ładowanie...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center text-red-600">
            {error}
          </div>
        ) : goals.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Target className="w-12 h-12 mx-auto mb-2 text-gray-400" />
            <p>Brak celów dla wybranego okresu i operatora</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Okres
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Grupa
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Typ
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    KAM
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cel MIN
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cel TARGET
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Akcje
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {goals.map((goal) => (
                  <tr key={goal.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-900">
                          {MONTHS.find(m => m.value === goal.miesiac)?.label} {goal.rok}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                        {goal.product_group || '—'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {goal.dodinfo ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          <Users className="w-3 h-3" />
                          Indywidualny
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          <TrendingUp className="w-3 h-3" />
                          Zespołowy
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {goal.dodinfo ? (
                        <span className="text-sm text-gray-900">
                          {getKamName(goal.dodinfo)}
                          <span className="text-gray-500 ml-1">({goal.dodinfo})</span>
                        </span>
                      ) : (
                        <span className="text-sm text-gray-500">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className="text-sm font-medium text-gray-900">
                        {formatValue(goal.wartosc)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className="text-sm font-medium text-gray-900">
                        {goal.wartosc_target ? formatValue(goal.wartosc_target) : <span className="text-gray-400">= MIN</span>}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEditGoal(goal)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                        title="Edytuj"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(goal)}
                        className="text-red-600 hover:text-red-900"
                        title="Usuń"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <GoalModal
          goal={editingGoal}
          operatorCode={selectedOperator}
          productGroup={selectedProductGroup}
          kams={kams}
          onSave={handleSaveGoal}
          onClose={() => {
            setShowModal(false)
            setEditingGoal(null)
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Potwierdzenie usunięcia
            </h3>
            <p className="text-gray-600 mb-6">
              Czy na pewno chcesz usunąć cel dla{' '}
              {MONTHS.find(m => m.value === deleteConfirm.miesiac)?.label} {deleteConfirm.rok}
              {deleteConfirm.dodinfo && (
                <> - KAM: {getKamName(deleteConfirm.dodinfo)}</>
              )}?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Anuluj
              </button>
              <button
                onClick={() => handleDeleteGoal(deleteConfirm)}
                className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700"
              >
                Usuń
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Goal Modal Component
function GoalModal({ goal, operatorCode, productGroup, kams, onSave, onClose }) {
  const operator = OPERATORS.find(op => op.code === operatorCode)
  const supportsIndividualGoals = operator?.supportsIndividualGoals ?? true

  const [groups, setGroups] = useState([])
  const [formData, setFormData] = useState({
    miesiac: goal?.miesiac || new Date().getMonth() + 1,
    rok: goal?.rok || new Date().getFullYear(),
    wartosc: goal?.wartosc || '',
    wartosc_target: goal?.wartosc_target || '',
    dodinfo: goal?.dodinfo || '',
    product_group: goal?.product_group || productGroup || 'DEFAULT',
  })

  useEffect(() => {
    if (!operatorCode || operatorCode === 'TOTAL') return
    apiService.getProductGroups(operatorCode).then(res => {
      const loaded = res.data?.groups || []
      setGroups(loaded)
      // If current product_group is not valid for this operator, pick the first available
      const validCodes = loaded.map(g => g.code)
      if (validCodes.length > 0 && !validCodes.includes(formData.product_group)) {
        const aggregate = loaded.find(g => g.is_aggregate)
        setFormData(prev => ({
          ...prev,
          product_group: aggregate?.code || validCodes[0],
        }))
      }
    }).catch(() => {})
  }, [operatorCode])
  const [errors, setErrors] = useState({})

  const validate = () => {
    const newErrors = {}

    if (!formData.wartosc || formData.wartosc <= 0) {
      newErrors.wartosc = 'Wartość celu musi być większa od 0'
    }

    if (formData.wartosc_target && parseFloat(formData.wartosc_target) < parseFloat(formData.wartosc)) {
      newErrors.wartosc_target = 'Cel TARGET musi być >= cel MIN'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    
    if (!validate()) {
      return
    }

    onSave({
      miesiac: parseInt(formData.miesiac),
      rok: parseInt(formData.rok),
      wartosc: parseFloat(formData.wartosc),
      wartosc_target: formData.wartosc_target ? parseFloat(formData.wartosc_target) : null,
      dodinfo: formData.dodinfo || null,
      product_group: formData.product_group,
    })
  }

  const currentYear = new Date().getFullYear()
  const years = [currentYear - 1, currentYear, currentYear + 1, currentYear + 2]

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {goal ? 'Edytuj cel' : 'Dodaj nowy cel'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Operator (display only) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Operator
              </label>
              <input
                type="text"
                value={OPERATORS.find(o => o.code === operatorCode)?.name || operatorCode}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
              />
            </div>

            {/* Product Group */}
            {groups.length > 1 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Grupa produktów *
                </label>
                <select
                  value={formData.product_group}
                  onChange={(e) => setFormData({ ...formData, product_group: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {groups.map(g => (
                    <option key={g.code} value={g.code}>{g.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Month */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Miesiąc *
              </label>
              <select
                value={formData.miesiac}
                onChange={(e) => setFormData({ ...formData, miesiac: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                {MONTHS.map(month => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Year */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rok *
              </label>
              <select
                value={formData.rok}
                onChange={(e) => setFormData({ ...formData, rok: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                {years.map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            {/* Goal Value */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Wartość celu *
              </label>
              <input
                type="number"
                value={formData.wartosc}
                onChange={(e) => setFormData({ ...formData, wartosc: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.wartosc ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Wprowadź wartość celu"
                min="0"
                step="0.01"
                required
              />
              {errors.wartosc && (
                <p className="mt-1 text-sm text-red-600">{errors.wartosc}</p>
              )}
            </div>

            {/* Goal Value Target (optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Wartość celu TARGET (opcjonalnie)
              </label>
              <input
                type="number"
                value={formData.wartosc_target}
                onChange={(e) => setFormData({ ...formData, wartosc_target: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.wartosc_target ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Zostaw puste = cel MIN"
                min="0"
                step="0.01"
              />
              {errors.wartosc_target && (
                <p className="mt-1 text-sm text-red-600">{errors.wartosc_target}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Cel docelowy. Jeśli puste, target = minimum.
              </p>
            </div>

            {/* KAM Code (optional) - only for operators that support it */}
            {supportsIndividualGoals ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  KAM (opcjonalne - pozostaw puste dla celu zespołowego)
                </label>
                <select
                  value={formData.dodinfo}
                  onChange={(e) => setFormData({ ...formData, dodinfo: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">-- Cel zespołowy (brak KAMa) --</option>
                  {kams.filter(k => k.is_active).map(kam => (
                    <option key={kam.kam_code} value={kam.kam_code}>
                      {kam.kam_name} ({kam.kam_code})
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Wybierz KAMa dla celu indywidualnego lub zostaw puste dla celu zespołowego
                </p>
              </div>
            ) : (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>ℹ️ Informacja:</strong> Operator {operator?.name} obsługuje tylko cele zespołowe (nie ma celów indywidualnych per KAM).
                </p>
              </div>
            )}
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Anuluj
            </button>
            <button
              type="submit"
              className="flex items-center gap-2 px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Save className="w-4 h-4" />
              Zapisz
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

